import { type Editor } from '@tiptap/react';
import { type TableOfContentData } from '@tiptap/extension-table-of-contents';
import { useEffect, useMemo, useState } from 'react';

import { isSameTocState, TOC_UPDATE_EVENT, type TocUpdateEvent } from '@/extensions/toc-events';
import { isEditorAlive } from '@/utils/editor-utils';

const EMPTY_TOC: TableOfContentData = [];

export const useEditorTocData = (editor: Editor | null, options?: { live?: boolean }) => {
  const live = options?.live ?? false;
  const [tocData, setTocData] = useState<TableOfContentData>(() => readTocState(editor));
  const [prevEditor, setPrevEditor] = useState(editor);

  if (editor !== prevEditor) {
    setPrevEditor(editor);
    setTocData((previous) => (editor ? selectTocData(previous, readTocState(editor), live) : EMPTY_TOC));
  }

  useEffect(() => {
    const eventTarget = getTocEvents(editor)?.events;
    if (!eventTarget) {
      return;
    }
    // Updates published while closed were held back, so opening catches up from the editor.
    if (live) {
      setTocData((previous) => selectTocData(previous, readTocState(editor), true));
    }
    const handleTocUpdate = (event: Event) => {
      const next = (event as TocUpdateEvent).detail;
      setTocData((previous) => selectTocData(previous, next, live));
    };

    eventTarget.addEventListener(TOC_UPDATE_EVENT, handleTocUpdate);
    return () => {
      eventTarget.removeEventListener(TOC_UPDATE_EVENT, handleTocUpdate);
    };
  }, [editor, live]);

  return useMemo(() => tocData.filter((item) => !!item.textContent?.trim()), [tocData]);
};

// tiptap's destroy() wipes extensionStorage but leaves the editor reference truthy, so every read
// has to treat a destroyed instance as absent — these run during render, where a throw is fatal.
const getTocEvents = (editor: Editor | null) => (isEditorAlive(editor) ? editor.storage.tocEvents : undefined);

const readTocState = (editor: Editor | null): TableOfContentData => getTocEvents(editor)?.current ?? EMPTY_TOC;

const selectTocData = (previous: TableOfContentData, next: TableOfContentData, live: boolean) => {
  if (isSameTocState(previous, next)) {
    return previous;
  }

  if (!live && hasVisibleHeadings(previous) === hasVisibleHeadings(next)) {
    return previous;
  }

  return next;
};

const hasVisibleHeadings = (items: TableOfContentData) => items.some((item) => !!item.textContent?.trim());
